import { useEffect, useRef, useState, type FormEvent } from 'react'
import { AlertCircle, ShieldCheck, Fingerprint } from 'lucide-react'
import { startAuthentication } from '@simplewebauthn/browser'
import { AuthShell } from './AuthShell.js'
import { homeRoute } from '../prefs.js'
import { submitMfa, getMfaMethods, passkeyMfaOptions, passkeyMfaVerify, type MfaMethods } from './api.js'
import { runLoginSuccess } from '../shell/auth-motion.js'
import { consumeReturnTo } from '../shell/auth-routing.js'

/**
 * /login/mfa — deuxième facteur (LOT 5.5.1).
 *
 * Le compte choisit son arme : CLÉ D'ACCÈS (passkey), application TOTP, ou
 * code de secours. L'écran interroge d'abord les méthodes réellement
 * disponibles sur le compte en cours de connexion, ouvre la méthode
 * PRÉFÉRÉE, et laisse basculer vers les autres — rien n'est supprimé,
 * les codes de secours restent l'issue de secours.
 */

async function destination(): Promise<string> {
  let home = '/'
  try {
    const pr = await fetch('/api/v1/me/preferences', { credentials: 'same-origin' }).then((x) => x.json()) as { resolved: Record<string, string> }
    home = homeRoute(pr.resolved)
  } catch { /* accueil par défaut */ }
  // LOT 6 : le return_to stocké au passage par /login prime sur l'accueil.
  return consumeReturnTo(home)
}

async function finishLogin(state: string | undefined): Promise<void> {
  if (state === 'PASSWORD_CHANGE_REQUIRED') { window.location.assign('/login/new-password'); return }
  await runLoginSuccess(await destination())
}

export function MfaPage() {
  const [methods, setMethods] = useState<MfaMethods | null>(null)
  const [mode, setMode] = useState<'passkey' | 'totp' | 'recovery'>('totp')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void getMfaMethods().then((m) => {
      if (!m) { window.location.assign('/login'); return }
      setMethods(m)
      // Méthode préférée d'abord ; passkey par défaut si c'est la seule.
      if (m.passkey && (m.preferred === 'passkey' || !m.totp)) setMode('passkey')
      else setMode('totp')
    })
  }, [])

  useEffect(() => { if (mode !== 'passkey') inputRef.current?.focus() }, [mode])

  const passkeySupported = typeof window.PublicKeyCredential !== 'undefined'

  const runPasskey = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const options = await passkeyMfaOptions()
      if (!options) { setError('Impossible de démarrer la vérification — réessayez.'); return }
      const assertion = await startAuthentication({ optionsJSON: options as never })
      const r = await passkeyMfaVerify(assertion)
      if (r.ok) { await finishLogin(r.state); return }
      if (r.error === 'NO_PENDING') { window.location.assign('/login'); return }
      setError('Clé d’accès refusée — réessayez ou utilisez une autre méthode.')
    } catch (e) {
      const name = (e as { name?: string }).name
      setError(name === 'NotAllowedError'
        ? 'Vérification annulée ou expirée — réessayez.'
        : 'La vérification par clé d’accès a échoué dans ce navigateur.')
    } finally {
      setBusy(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy || !code.trim()) return
    setBusy(true)
    setError(null)
    try {
      const r = await submitMfa(code.trim(), mode === 'recovery' ? 'recovery' : 'totp')
      if (r.ok) { await finishLogin(r.state); return }
      if (r.error === 'NO_PENDING') { window.location.assign('/login'); return }
      setError(mode === 'recovery' ? 'Ce code de secours n’est pas valide.' : 'Code incorrect. Vérifiez l’heure de votre téléphone.')
      setCode('')
      inputRef.current?.focus()
    } catch {
      setError('Le serveur est injoignable. Réessayez dans un instant.')
    } finally {
      setBusy(false)
    }
  }

  const totp = mode === 'totp'
  const subtitle = mode === 'passkey' ? 'Confirmez avec votre clé d’accès'
    : totp ? 'Saisissez le code de votre application' : 'Saisissez un code de secours'

  const switcher = (
    <p className="lc-auth__alt" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {methods?.passkey && mode !== 'passkey' && passkeySupported ? (
        <button type="button" className="lc-auth__link" onClick={() => { setMode('passkey'); setError(null) }}>
          Utiliser une clé d’accès
        </button>
      ) : null}
      {methods?.totp && mode !== 'totp' ? (
        <button type="button" className="lc-auth__link" onClick={() => { setMode('totp'); setCode(''); setError(null) }}>
          Utiliser l’application d’authentification
        </button>
      ) : null}
      {mode !== 'recovery' ? (
        <button type="button" className="lc-auth__link" onClick={() => { setMode('recovery'); setCode(''); setError(null) }}>
          Utiliser un code de secours
        </button>
      ) : null}
    </p>
  )

  return (
    <AuthShell title="Vérification" subtitle={subtitle}>
      {error ? (
        <p className="lc-form__error" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          {error}
        </p>
      ) : null}

      {mode === 'passkey' ? (
        <div className="lc-form">
          {!passkeySupported ? (
            <p className="lc-form__help">Ce navigateur ne prend pas en charge les clés d’accès — utilisez une autre méthode.</p>
          ) : (
            <button className="lc-auth__cta" type="button" onClick={() => void runPasskey()} disabled={busy}>
              {busy ? <span className="lc-spin" aria-hidden="true" /> : <Fingerprint size={17} aria-hidden="true" />}
              {busy ? 'Vérification…' : 'Utiliser ma clé d’accès'}
            </button>
          )}
          {switcher}
        </div>
      ) : (
        <form className="lc-form" onSubmit={submit} noValidate>
          <div className="lc-field">
            <label className="lc-label" htmlFor="mfa-code">
              {totp ? 'Code à 6 chiffres' : 'Code de secours'}
            </label>
            <input
              ref={inputRef}
              id="mfa-code"
              className={totp ? 'lc-otp' : 'lc-input'}
              type="text"
              inputMode={totp ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              spellCheck={false}
              maxLength={totp ? 6 : 24}
              placeholder={totp ? '······' : 'XXXX-XXXX-XXXX-XXXX'}
              value={code}
              onChange={(e) => setCode(totp ? e.target.value.replace(/\D/g, '') : e.target.value.toUpperCase())}
              disabled={busy}
              required
            />
            {!totp ? (
              <p className="lc-form__help">
                Chaque code de secours ne fonctionne qu’une seule fois.
              </p>
            ) : null}
          </div>

          <button className="lc-auth__cta" type="submit" disabled={busy || !code.trim()}>
            {busy ? <span className="lc-spin" aria-hidden="true" /> : <ShieldCheck size={17} aria-hidden="true" />}
            {busy ? 'Vérification…' : 'Vérifier'}
          </button>
          {switcher}
        </form>
      )}
    </AuthShell>
  )
}
